// @noflow
import * as React from "react";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import * as Auth from "../../../lib/Auth";

import {
    dateFromDayShift,
    fromShiftTag,
    niceDate,
    shiftNumber,
    dateFromWeekAndShift,
    niceNumber,
    weekNumber,
} from "../../../lib/Util";
import { translate } from "../../../components/IntlProviderWrapper";
import { FIELD_IS_TIME_SERIES_VISIBLE } from "../../../lib/LocalSettings";

import type {
    ILineData,
    IPreplanningDataExpectedProduction,
    IPreplanningDataLine,
} from "../../../lib/backend/manufacturing2.generated.types";
import type { ILabelValuePair } from "../../../lib/Util";
import local_settings from "../../../lib/LocalSettings";

import "./PreplanningChart.scss";
import ErrorComponent from "../../../components/ErrorComponent";
import MultiSelect from "../../../components/MultiSelect";
import { Toolbar, ToolbarGroup, ToolbarItem } from "../../../components/Toolbar";
import { getBackend } from "../../../lib/backend/Backend2";

enum PRODUCTION_VIEW {
    day = "day",
    week = "week",
    month = "month",
}

enum TIME_SERIES_NAME {
    produced = "produced",
    produced_cumulative = "produced_cumulative",
    produced_planned = "produced_planned",
    produced_planned_cumulative = "produced_planned_cumulative",
    expected = "expected",
    expected_cumulative = "expected_cumulative",
}

type TimeSeriesVisibilityHandler = () => void;
type IsTimeSeriesVisibleState = Record<TIME_SERIES_NAME, boolean>;

type DataPoint = {
    name: string;
    y: number;
};

type IProductGroupLine = {
    line_title: string;
    external_id: string;
};

type IProductGroup = {
    group_name: string;
    lines: IProductGroupLine[];
};

type IProductGroupTemplate = {
    linegroup_uuid: string;
    group_templates: IProductGroup[];
};

type IProductGroupTemplatesData = {
    templates: IProductGroupTemplate[];
    version: number;
};

const product_group_templates_filename = "preplanning_production_chart_groups.json";

type Props = {
    date: Date;
    expected_productions: IPreplanningDataExpectedProduction[];
    lines: ILineData[];
    line_material_group_map: Map<string, string>;
    preplanning_lines: IPreplanningDataLine[];
    linegroup_uuid: string;
};

type State = {
    error: string;
    is_time_series_visible: IsTimeSeriesVisibleState;
    selected_view: PRODUCTION_VIEW;
    selected_product_group: string;
    selected_product_group_lines: ILabelValuePair[];
    selected_material_groups: ILabelValuePair[];
    create_edit_product_group_modal: boolean;
    create_edit_product_group: string;
    create_edit_product_group_lines: ILabelValuePair[];
    create_edit_product_group_error: string;
    is_editing: boolean;
    is_delete: boolean;
    product_group_templates: IProductGroup[];
};

const loadIsTimeSeriesVisible = (): IsTimeSeriesVisibleState => {
    const is_time_series_visible: IsTimeSeriesVisibleState = {
        [TIME_SERIES_NAME.produced]: true,
        [TIME_SERIES_NAME.produced_cumulative]: true,
        [TIME_SERIES_NAME.produced_planned]: false,
        [TIME_SERIES_NAME.produced_planned_cumulative]: false,
        [TIME_SERIES_NAME.expected]: true,
        [TIME_SERIES_NAME.expected_cumulative]: true,
    };
    const is_time_series_visible_string = local_settings.getItem(FIELD_IS_TIME_SERIES_VISIBLE);
    if (is_time_series_visible_string != null) {
        try {
            const is_time_series_visible_obj: typeof TIME_SERIES_NAME = JSON.parse(is_time_series_visible_string);
            if (typeof is_time_series_visible_obj === "object" && is_time_series_visible_obj !== null) {
                for (const key of Object.keys(is_time_series_visible_obj)) {
                    if (is_time_series_visible_obj[key] !== undefined && key in TIME_SERIES_NAME) {
                        is_time_series_visible[key as TIME_SERIES_NAME] = is_time_series_visible_obj[key];
                    }
                }
            }
        } catch (error: unknown) {
            // Failed to parse JSON from local storage
        }
    }

    return is_time_series_visible;
};

const storeIsTimeSeriesVisible = (is_time_series_visible: IsTimeSeriesVisibleState) => {
    local_settings.setItem(FIELD_IS_TIME_SERIES_VISIBLE, JSON.stringify(is_time_series_visible));
};

const getLineExternalId = (line_id: string): string => {
    return `058${line_id}`;
};

const getStartDate = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const start_date = new Date(year, month, 1);
    const shift_start_date = dateFromDayShift(start_date, 0);
    return shift_start_date;
};

const getEndDate = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const end_date = new Date(year, month + 1, 0);
    const shift_end_date = dateFromDayShift(end_date, 0);
    return shift_end_date;
};

const cumulateTimeSeries = (data: DataPoint[]): DataPoint[] => {
    const cumulated_data: DataPoint[] = [];
    let cumulated_value = 0;
    for (const data_item of data) {
        cumulated_value += data_item.y;
        cumulated_data.push({ name: data_item.name, y: cumulated_value });
    }

    return cumulated_data;
};

const getExpectedTimeSeries = (
    expected_production: number,
    start_time: Date,
    end_time: Date,
    view: PRODUCTION_VIEW
): DataPoint[] => {
    const expected_time_series: DataPoint[] = [];
    const labels = getTimeLabels(start_time, end_time, view);
    const average_expected_production = expected_production / labels.length;
    for (let i = 0; i < labels.length; i++) {
        expected_time_series.push({ name: labels[i], y: average_expected_production });
    }

    return expected_time_series;
};

const getTimeLabel = (date: Date, view: PRODUCTION_VIEW): string => {
    const shift_number = shiftNumber(date);
    if (view === PRODUCTION_VIEW.week) {
        return `${shift_number.year}/${shift_number.week}`;
    } else if (view === PRODUCTION_VIEW.month) {
        return `${translate(`common.month${date.getMonth()}`)} ${date.getFullYear()}`;
    }

    return niceDate(dateFromWeekAndShift(shift_number.week, shift_number.year, shift_number.shift));
};

const getTimeLabels = (start_date: Date, end_date: Date, view: PRODUCTION_VIEW): string[] => {
    const labels: string[] = [];
    if (end_date < start_date) {
        return labels;
    }

    let date = new Date(start_date);
    if (view === PRODUCTION_VIEW.week) {
        const end_year = end_date.getFullYear();
        const end_week = weekNumber(end_date);
        let year = date.getFullYear();
        let week = weekNumber(date);
        while (year <= end_year && week <= end_week) {
            labels.push(getTimeLabel(date, view));
            date.setDate(date.getDate() + 7);
            year = date.getFullYear();
            week = weekNumber(date);
        }

        return labels;
    } else if (view === PRODUCTION_VIEW.month) {
        const end_year = end_date.getFullYear();
        const end_month = end_date.getMonth();
        let year = date.getFullYear();
        let month = date.getMonth();
        while (year <= end_year && month <= end_month) {
            labels.push(getTimeLabel(date, view));
            date.setMonth(date.getMonth() + 1);
            year = date.getFullYear();
            month = date.getMonth();
        }

        return labels;
    }

    while (date <= end_date) {
        labels.push(getTimeLabel(date, view));
        date.setDate(date.getDate() + 1);
    }

    return labels;
};

export const PreplanningChartModal = (props: Props & { show: boolean; onClose: () => void }) => {
    const { show, onClose } = props;
    return (
        <Modal
            size="lg"
            className="preplanning-chart__modal"
            dialogClassName="modal-dialog-scrollable modal-max h-100"
            show={show}
            onHide={onClose}
            animation={false}
        >
            <Modal.Header>
                <Modal.Title>{translate("common.production", "Production")}</Modal.Title>
                <button type="button" className="close" onClick={onClose}>
                    <span>×</span>
                </button>
            </Modal.Header>
            <Modal.Body className="p-0">
                <PreplanningChart {...props} />
            </Modal.Body>
        </Modal>
    );
};

/**
 * Validates and filters template content from blob storage
 * @param {string} content - The raw content from blob storage
 * @returns {IProductGroupTemplatesData | null} - Valid template data or null if no valid data found
 */
function validateTemplateContent(content: string): IProductGroupTemplatesData | null {
    // Check if the content is not empty
    if (!content.trim()) {
        console.error("Content is empty");
        return null;
    }

    let parsed_data;

    // Try to parse JSON
    try {
        parsed_data = JSON.parse(content);
    } catch (error: unknown) {
        console.error(`JSON parsing failed: ${(error as Error).message}`);
        return null;
    }

    // Validate root structure
    if (!parsed_data || typeof parsed_data !== "object") {
        console.error(`Parsed data is not an object, skipping - %o`, parsed_data);
        return null;
    }

    // Initialize result structure
    const result: IProductGroupTemplatesData = {
        version: 0,
        templates: [],
    };

    // Validate and set version
    if (typeof parsed_data.version === "number" && parsed_data.version >= 0) {
        result.version = parsed_data.version;
    } else {
        console.error(`Invalid version in parsed data, expected a non-negative number, got - ${parsed_data.version}`);
    }

    // Process templates array
    if (!Array.isArray(parsed_data.templates)) {
        console.error(`templates must be an array, skipping templates - ${parsed_data.templates}`);
        return result;
    }

    // Process each template
    for (let i = 0; i < parsed_data.templates.length; i++) {
        const template = parsed_data.templates[i];

        if (!template || typeof template !== "object") {
            console.error(`Template at index ${i} is not an object, skipping template - %o`, template);
            continue;
        }

        // Validate linegroup_uuid
        if (typeof template.linegroup_uuid !== "string") {
            console.error(`Invalid linegroup_uuid in template at index ${i}, skipping template - %o`, template);
            continue;
        }

        // Validate group_templates array
        if (!Array.isArray(template.group_templates)) {
            console.error(
                `group_templates must be an array in template at index ${i}, skipping template - %o`,
                template
            );
            continue;
        }

        const valid_template: IProductGroupTemplate = {
            linegroup_uuid: template.linegroup_uuid,
            group_templates: [],
        };

        // Process each group
        for (let j = 0; j < template.group_templates.length; j++) {
            const group = template.group_templates[j];

            if (!group || typeof group !== "object") {
                console.error(
                    `Group at index ${j} in template ${template.linegroup_uuid} is not an object, skipping group - %o`,
                    group
                );
                continue;
            }

            // Validate group_name
            if (typeof group.group_name !== "string" || !group.group_name.trim()) {
                console.error(
                    `Invalid group_name in group ${j} of template ${template.linegroup_uuid}, skipping group - %o`,
                    group
                );
                continue;
            }

            // Validate lines array
            if (!Array.isArray(group.lines)) {
                console.error(
                    `Lines must be an array in group ${j} of template ${template.linegroup_uuid}, skipping group - %o`,
                    group
                );
                continue;
            }

            const valid_group: IProductGroup = {
                group_name: group.group_name,
                lines: [],
            };

            // Process each line
            for (let k = 0; k < group.lines.length; k++) {
                const line = group.lines[k];

                if (!line || typeof line !== "object") {
                    console.error(
                        `Line at index ${k} in group ${group.group_name} of template ${template.linegroup_uuid} is not an object, skipping line - %o`,
                        line
                    );
                    continue;
                }

                // Validate line_title
                if (typeof line.line_title !== "string" || !line.line_title.trim()) {
                    console.error(
                        `Invalid line_title in line ${k} of group ${group.group_name} in template ${template.linegroup_uuid}, skipping line - %o`,
                        line
                    );
                    continue;
                }

                // Validate external_id
                if (typeof line.external_id !== "string") {
                    console.error(
                        `Invalid external_id in line ${k} of group ${group.group_name} in template ${template.linegroup_uuid}, skipping line - %o`,
                        line
                    );
                    continue;
                }

                // Line is valid, add it
                valid_group.lines.push({
                    line_title: line.line_title,
                    external_id: line.external_id,
                });
            }

            // Only add the group if it has at least one valid line
            if (valid_group.lines.length > 0) {
                valid_template.group_templates.push(valid_group);
            } else {
                console.error(`Group ${j} in template ${i} has no valid lines, skipping group - %o`, group);
            }
        }

        // Only add the template if it has at least one valid group
        if (valid_template.group_templates.length > 0) {
            result.templates.push(valid_template);
        }
    }

    return result;
}

class PreplanningChart extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const all_selected_groups = this.setSelectAllLines();
        const state: State = {
            error: "",
            is_time_series_visible: loadIsTimeSeriesVisible(),
            selected_view: PRODUCTION_VIEW.day,
            selected_product_group: all_selected_groups?.selected_product_group ?? "",
            selected_product_group_lines: all_selected_groups?.selected_product_group_lines ?? [],
            selected_material_groups: all_selected_groups?.selected_material_groups ?? [],
            create_edit_product_group_modal: false,
            create_edit_product_group: "",
            create_edit_product_group_lines: [],
            create_edit_product_group_error: "",
            is_editing: false,
            is_delete: false,
            product_group_templates: [],
        };
        this.state = state;
    }

    async componentDidMount() {
        await this.loadData();
    }

    loadData = async () => {
        try {
            const product_group_templates = await this.loadProductGroupTemplates();
            this.setState({ product_group_templates });
        } catch (error: unknown) {
            console.error("Failed to load product group templates:", error);
            this.setState({ error: translate("common.something_went_wrong", "Something went wrong") });
        }
    };

    getProductGroupTemplates = async (): Promise<IProductGroupTemplatesData> => {
        const fallback: IProductGroupTemplatesData = { version: 1, templates: [] };

        try {
            const { content } = await getBackend().common.getBlobStorageItemAsStringOrNull({
                key: product_group_templates_filename,
            });

            this.setState({ error: "" });

            if (!content) {
                return fallback;
            }

            const valid_config = validateTemplateContent(content);

            if (!valid_config) {
                this.setState({ error: translate("common.something_went_wrong", "Something went wrong") });
                return fallback;
            }

            return valid_config;
        } catch (err: unknown) {
            this.setState({ error: (err as Error).message });
            return fallback;
        }
    };

    loadProductGroupTemplates = async (): Promise<IProductGroup[]> => {
        const { templates } = await this.getProductGroupTemplates();
        const linegroup_uuid = this.props.linegroup_uuid;
        const match = templates.find(t => t.linegroup_uuid === linegroup_uuid);

        return match?.group_templates ?? [];
    };

    getMeasurementUnit = (): string => {
        // TODO: get measurement unit
        const measurement_unit = "";
        return measurement_unit;
    };

    getTimeSeriesVisibilityHandler = (name: TIME_SERIES_NAME, is_visible: boolean): TimeSeriesVisibilityHandler => {
        return () => {
            const is_time_series_visible: IsTimeSeriesVisibleState = {
                ...this.state.is_time_series_visible,
                [String(name)]: is_visible,
            };
            this.setState({
                is_time_series_visible,
            });
            storeIsTimeSeriesVisible(is_time_series_visible);
        };
    };

    getSelectedPreplanningLines = (): IPreplanningDataLine[] => {
        const { preplanning_lines, line_material_group_map } = this.props;
        const { selected_product_group_lines } = this.state;
        const { selected_material_groups } = this.state;
        const preplanning_lines_map: Map<string, IPreplanningDataLine> = new Map(
            preplanning_lines.map(line => [line.line_external_id, line])
        );
        const selected_preplanning_lines: IPreplanningDataLine[] = [];
        for (const [line_id, material_group] of line_material_group_map) {
            if (selected_product_group_lines.some(line => line.value === getLineExternalId(line_id))) {
                if (selected_material_groups.some(group => group.value === material_group)) {
                    const preplanning_line = preplanning_lines_map.get(getLineExternalId(line_id));
                    if (preplanning_line !== undefined) {
                        selected_preplanning_lines.push(preplanning_line);
                    }
                }
            }
        }

        return selected_preplanning_lines;
    };

    getPlannedProducedTimeSeries = (): { planned: DataPoint[]; produced: DataPoint[] } => {
        const { date } = this.props;
        const { selected_view } = this.state;
        const selected_preplanning_lines = this.getSelectedPreplanningLines();
        const start_date = getStartDate(date);
        const end_date = getEndDate(date);
        const time_labels = getTimeLabels(start_date, end_date, selected_view);
        const planned_map: Map<string, number> = new Map();
        const produced_map: Map<string, number> = new Map();
        for (const time_label of time_labels) {
            planned_map.set(time_label, 0);
            produced_map.set(time_label, 0);
        }

        for (const preplanning_line of selected_preplanning_lines) {
            const { materials } = preplanning_line;
            for (const material of materials) {
                const { planned, produced } = material;
                for (const { quantity, shift_tag } of planned) {
                    const shift_label = getTimeLabel(fromShiftTag(shift_tag), selected_view);
                    const current_quantity = planned_map.get(shift_label) || 0;
                    planned_map.set(shift_label, current_quantity + quantity);
                }

                for (const { quantity, shift_tag } of produced) {
                    const shift_label = getTimeLabel(fromShiftTag(shift_tag), selected_view);
                    const current_quantity = produced_map.get(shift_label) || 0;
                    produced_map.set(shift_label, current_quantity + quantity);
                }
            }
        }

        const planned_ts: DataPoint[] = [];
        for (const [label, quantity] of planned_map) {
            planned_ts.push({
                name: label,
                y: quantity,
            });
        }

        const produced_ts: DataPoint[] = [];
        for (const [label, quantity] of produced_map) {
            produced_ts.push({
                name: label,
                y: quantity,
            });
        }

        return {
            planned: planned_ts,
            produced: produced_ts,
        };
    };

    getExpectedProduction = (): number => {
        const { expected_productions } = this.props;
        const { selected_material_groups } = this.state;
        const selected_group_values = selected_material_groups.map(group => group.value);

        const matching_productions = expected_productions.filter(production =>
            selected_group_values.includes(production.material_group)
        );

        const total_quantity = matching_productions.reduce((accumulated_total, production) => {
            const production_quantity = production.quantity || 0;
            return accumulated_total + production_quantity;
        }, 0);

        return total_quantity;
    };

    getChartOptions = (): Highcharts.Options => {
        const { date } = this.props;
        const { is_time_series_visible, selected_view } = this.state;

        const shift_start_date = getStartDate(date);
        const shift_end_date = getEndDate(date);

        const { planned: planned_ts, produced: produced_ts } = this.getPlannedProducedTimeSeries();
        const produced_cumulative_ts: DataPoint[] = cumulateTimeSeries(produced_ts);
        const planned_cumulative_ts: DataPoint[] = cumulateTimeSeries(planned_ts);
        const series: Highcharts.SeriesOptionsType[] = [
            {
                name: translate("Production.produced", "Produced"),
                type: "column",
                color: "#7cb5ec",
                data: produced_ts,
                yAxis: 0,
                visible: is_time_series_visible[TIME_SERIES_NAME.produced],
                events: {
                    hide: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.produced, false),
                    show: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.produced, true),
                },
            },
            {
                name: translate("Production.produced_cumulative", "Produced cumulative"),
                type: "line",
                color: "#5e8aee",
                data: produced_cumulative_ts,
                yAxis: 1,
                visible: is_time_series_visible[TIME_SERIES_NAME.produced_cumulative],
                events: {
                    hide: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.produced_cumulative, false),
                    show: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.produced_cumulative, true),
                },
            },
            {
                name: translate("Production.planned", "Planned"),
                type: "column",
                color: "#54d1db",
                data: planned_ts,
                yAxis: 0,
                visible: is_time_series_visible[TIME_SERIES_NAME.produced_planned],
                events: {
                    hide: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.produced_planned, false),
                    show: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.produced_planned, true),
                },
            },
            {
                name: translate("Production.planned_cumulative", "Planned cumulative"),
                type: "line",
                color: "#2dcca7",
                data: planned_cumulative_ts,
                yAxis: 1,
                visible: is_time_series_visible[TIME_SERIES_NAME.produced_planned_cumulative],
                events: {
                    hide: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.produced_planned_cumulative, false),
                    show: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.produced_planned_cumulative, true),
                },
            },
        ];

        const expected_production = this.getExpectedProduction();
        if (expected_production !== undefined && expected_production > 0) {
            const expected_ts = getExpectedTimeSeries(
                expected_production,
                shift_start_date,
                shift_end_date,
                selected_view
            );
            const expected_cumulative_ts = cumulateTimeSeries(expected_ts);
            series.push({
                name: translate("Production.expected", "Expected"),
                type: "line",
                color: "#f7c948",
                data: expected_ts,
                yAxis: 0,
                visible: is_time_series_visible[TIME_SERIES_NAME.expected],
                events: {
                    hide: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.expected, false),
                    show: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.expected, true),
                },
            });

            series.push({
                name: translate("Production.expected_cumulative", "Expected cumulative"),
                type: "line",
                color: "#f86a6a",
                data: expected_cumulative_ts,
                yAxis: 1,
                visible: is_time_series_visible[TIME_SERIES_NAME.expected_cumulative],
                events: {
                    hide: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.expected_cumulative, false),
                    show: this.getTimeSeriesVisibilityHandler(TIME_SERIES_NAME.expected_cumulative, true),
                },
            });
        }

        const measurement_unit = this.getMeasurementUnit();

        return {
            chart: {
                numberFormatter: (y: number) => niceNumber(y, 2),
            },
            xAxis: {
                type: "category",
                tickWidth: 1,
            },
            yAxis: [
                {
                    title: {
                        text: `${translate("common.quantity", "Quantity")} ${
                            measurement_unit !== "" ? `[${measurement_unit}]` : ""
                        }`,
                    },
                },
                {
                    title: undefined,
                    opposite: true,
                },
            ],
            series,
            title: {
                text: translate("Production.cumulative_production", "Cumulative production"),
            },
            subtitle: {
                text: `${niceDate(shift_start_date)} - ${niceDate(shift_end_date)}`,
            },
            tooltip: {
                shared: true,
                useHTML: true,
                valueSuffix: measurement_unit !== "" ? ` ${measurement_unit}` : "",
                formatter: function () {
                    const points = this.points ?? [];
                    const date = points[0].key;
                    let tooltip = `<span class="tooltip-date">${date}</span><br/>`;
                    for (const point of points) {
                        const color = point.color;
                        const series_name: string = point.series.name;
                        const value: number = point.y;
                        tooltip += `<span style="color: ${color}">\u25CF</span> ${series_name}: <b>${niceNumber(
                            value,
                            2
                        )} ${measurement_unit}</b><br/>`;
                    }

                    return tooltip;
                },
            },
        };
    };

    getMaterialGroupOptions = (selected_product_group_lines: ILabelValuePair[]): ILabelValuePair[] => {
        const { line_material_group_map } = this.props;
        const group_options: ILabelValuePair[] = [];
        const material_groups: Set<string> = new Set();
        const line_ids = selected_product_group_lines.map(line => line.value);
        const selected_material_group_map = new Map(
            [...line_material_group_map].filter(([line_id]) => line_ids.includes(getLineExternalId(line_id)))
        );
        for (const value of selected_material_group_map.values()) {
            if (!material_groups.has(value)) {
                material_groups.add(value);
                group_options.push({
                    label: value,
                    value,
                });
            }
        }

        return group_options;
    };

    handleProductGroupLineChange = (option: ILabelValuePair[]) => {
        const selected_product_group_lines = option.map(line => ({
            label: line.label,
            value: line.value,
        }));
        const group_options = this.getMaterialGroupOptions(selected_product_group_lines);
        this.setState({
            selected_product_group_lines,
            selected_material_groups: group_options,
        });
    };

    handleMaterialGroupChange = (option: ILabelValuePair[]) => {
        this.setState({
            selected_material_groups: option,
        });
    };

    setSelectAllLines = (): {
        selected_product_group: string;
        selected_product_group_lines: ILabelValuePair[];
        selected_material_groups: ILabelValuePair[];
    } => {
        const selected_product_group_lines = this.props.lines.map(line => ({
            label: line.title,
            value: line.external_id,
        }));
        const selected_material_groups = this.getMaterialGroupOptions(selected_product_group_lines);
        return {
            selected_product_group: "",
            selected_product_group_lines: selected_product_group_lines,
            selected_material_groups,
        };
    };

    setSelectAllLinesUpdateState = () => {
        const { selected_product_group, selected_product_group_lines, selected_material_groups } =
            this.setSelectAllLines();

        this.setState({
            selected_product_group,
            selected_product_group_lines,
            selected_material_groups,
        });
    };

    getProductGroupTemplateOptions = (): ILabelValuePair[] => {
        const { product_group_templates } = this.state;
        const product_group_template_options: ILabelValuePair[] = [];
        for (const group of product_group_templates) {
            product_group_template_options.push({
                label: group.group_name,
                value: group.group_name,
            });
        }
        return product_group_template_options;
    };

    getProductGroupTemplateLinesOptions = (): ILabelValuePair[] => {
        const { product_group_templates } = this.state;
        const selected_product_group = this.state.selected_product_group;
        const product_group_template_lines_options: ILabelValuePair[] = [];
        if (selected_product_group === "") {
            for (const line of this.props.lines) {
                product_group_template_lines_options.push({
                    label: line.title,
                    value: line.external_id,
                });
            }
            return product_group_template_lines_options;
        }
        const selected_product_group_lines =
            product_group_templates.find(group => group.group_name === selected_product_group)?.lines || [];
        for (const line of selected_product_group_lines) {
            product_group_template_lines_options.push({
                label: line.line_title,
                value: line.external_id,
            });
        }
        return product_group_template_lines_options;
    };

    handleGroupTemplateCreate = (is_editing: boolean = false) => {
        const new_state: Partial<State> = {
            create_edit_product_group_modal: true,
        };
        if (is_editing) {
            new_state.is_editing = true;
            new_state.create_edit_product_group = this.state.selected_product_group;
            new_state.create_edit_product_group_lines = this.state.selected_product_group_lines;
        }
        this.setState(new_state as State);
    };

    handleNewGroupTemplateClose = () => {
        this.resetCreateEditGroupState();
        this.setState({
            create_edit_product_group_modal: false,
        });
    };

    handleNewGroupTemplateSave = async () => {
        const { create_edit_product_group, create_edit_product_group_lines } = this.state;
        if (create_edit_product_group.trim() === "") {
            this.setState({
                create_edit_product_group_error: translate(
                    "Preplanning.product_group_template_name_empty",
                    "Product group template name is empty"
                ),
            });
            return;
        }
        if (create_edit_product_group_lines.length === 0) {
            this.setState({
                create_edit_product_group_error: translate(
                    "Preplanning.product_group_template_lines_empty",
                    "Product group template lines are empty"
                ),
            });
            return;
        }

        const { lines } = this.props;
        const selected_lines: IProductGroupLine[] = lines
            .filter(line => create_edit_product_group_lines.some(group => group.value === line.external_id))
            .map(line => ({
                line_title: line.title,
                external_id: line.external_id,
            }));

        const data = {
            group_name: create_edit_product_group,
            lines: selected_lines,
        };

        await this.handleCreateEditProductGroupTemplateSave(
            data,
            this.state.is_editing ? this.state.selected_product_group : undefined
        );
    };

    handleGroupTemplateChange = (option: ILabelValuePair) => {
        // Default value, all lines and material groups should be selected
        if (option === null) {
            this.setSelectAllLinesUpdateState();
            this.resetCreateEditGroupState();
            return;
        }
        const selected_product_group_lines =
            this.state.product_group_templates.find(group => group.group_name === option.value)?.lines ?? [];
        const selected_product_group_lines_pairs = selected_product_group_lines.map(line => ({
            label: line.line_title,
            value: line.external_id,
        }));
        const selected_material_groups = this.getMaterialGroupOptions(selected_product_group_lines_pairs);

        this.setState({
            selected_product_group: option.value,
            selected_product_group_lines: selected_product_group_lines_pairs,
            selected_material_groups: selected_material_groups,
        });
    };

    handleConfirmDeleteGroupTemplate = () => {
        this.setState({ is_delete: true });
    };

    handleDeleteGroupTemplate = async () => {
        const { selected_product_group } = this.state;
        await this.handleProductGroupTemplateDelete(selected_product_group);
        this.setSelectAllLinesUpdateState();
        this.setState({
            is_delete: false,
        });
    };

    resetCreateEditGroupState = () => {
        this.setState({
            create_edit_product_group: "",
            create_edit_product_group_lines: [],
            create_edit_product_group_error: "",
            is_editing: false,
        });
    };

    saveTemplateContentToBlobUpdateState = async (final_content: IProductGroupTemplatesData) => {
        const linegroup_uuid = this.props.linegroup_uuid;
        try {
            await getBackend().common.setBlobStorageItemAsString({
                key: product_group_templates_filename,
                content: JSON.stringify(final_content),
            });
            const new_product_group_templates =
                final_content.templates.find(t => t.linegroup_uuid === linegroup_uuid)?.group_templates ?? [];
            this.setState({ error: "", product_group_templates: new_product_group_templates });
        } catch (error: unknown) {
            console.error(error);
            this.setState({ error: translate("common.something_went_wrong", "Something went wrong") });
        }
    };

    handleCreateEditProductGroupTemplateSave = async (data: IProductGroup, edit_template?: string): Promise<void> => {
        let { templates, version } = await this.getProductGroupTemplates();

        const linegroup_uuid = this.props.linegroup_uuid;
        let line_group_templates: IProductGroupTemplate | void = templates.find(
            template => template.linegroup_uuid === linegroup_uuid
        );

        if (line_group_templates === undefined) {
            line_group_templates = {
                linegroup_uuid: linegroup_uuid,
                group_templates: [],
            };
        }
        const group_templates = line_group_templates.group_templates;
        const new_existing_template = group_templates.find(template => template.group_name === data.group_name);
        if (new_existing_template !== undefined && edit_template === undefined) {
            this.setState({
                create_edit_product_group_error: translate(
                    "Preplanning.product_group_template_exists",
                    "Product group template already exists"
                ),
            });
            return;
        }

        const existingIndex = group_templates.findIndex(template => template.group_name === edit_template);
        if (existingIndex !== -1) {
            group_templates[existingIndex] = data;
        } else {
            group_templates.push(data);
        }

        const final_content = {
            version: version,
            templates: [
                ...templates.filter(template => template.linegroup_uuid !== linegroup_uuid),
                {
                    linegroup_uuid: linegroup_uuid,
                    group_templates: group_templates,
                },
            ],
        };

        await this.saveTemplateContentToBlobUpdateState(final_content);
        this.setSelectAllLinesUpdateState();
        this.handleNewGroupTemplateClose();
    };

    handleProductGroupTemplateDelete = async (template_name: string) => {
        const file_content = await this.getProductGroupTemplates();
        const templates = file_content.templates;

        const linegroup_uuid = this.props.linegroup_uuid;
        const line_group_templates = templates.find(template => template.linegroup_uuid === linegroup_uuid);
        if (line_group_templates === undefined) {
            return;
        }

        line_group_templates.group_templates = line_group_templates.group_templates.filter(
            template => template.group_name !== template_name
        );

        const final_content = {
            version: file_content.version,
            templates: [
                ...templates.filter(template => template.linegroup_uuid !== linegroup_uuid),
                {
                    linegroup_uuid: linegroup_uuid,
                    group_templates: line_group_templates.group_templates,
                },
            ],
        };

        await this.saveTemplateContentToBlobUpdateState(final_content);
    };

    render() {
        const { selected_material_groups, selected_product_group_lines, error } = this.state;
        const group_options = this.getMaterialGroupOptions(selected_product_group_lines);
        const expected_production = this.getExpectedProduction();
        const product_group_template_options = this.getProductGroupTemplateOptions();
        const selected_product_group =
            product_group_template_options.find(group => group.value === this.state.selected_product_group) ?? "";
        const product_group_lines_options = this.getProductGroupTemplateLinesOptions();

        return (
            <div className="preplanning-chart">
                {error !== "" && <ErrorComponent className="m-3" type="error" msg={error} />}
                <Toolbar className="preplanning-chart__toolbar" drawerClassName="preplanning-chart__toolbar-drawer">
                    {Auth.roleCanEditPreplanning() && (
                        <ToolbarGroup id="preplanning-chart__toolbar-group-buttons">
                            <ToolbarItem>
                                <button
                                    className="btn btn-short btn-outline-secondary px-2"
                                    onClick={() => this.handleGroupTemplateCreate()}
                                >
                                    <i className="fas fa-plus align-middle" />
                                </button>
                            </ToolbarItem>
                            <ToolbarItem>
                                <button
                                    className="btn btn-short btn-outline-secondary px-2"
                                    onClick={() => this.handleGroupTemplateCreate(true)}
                                    disabled={this.state.selected_product_group === ""}
                                >
                                    <i className="fas fa-edit align-middle" />
                                </button>
                            </ToolbarItem>
                            <ToolbarItem>
                                <button
                                    className="btn btn-short btn-outline-secondary px-2"
                                    onClick={() => this.handleConfirmDeleteGroupTemplate()}
                                    disabled={this.state.selected_product_group === ""}
                                >
                                    <i className="fas fa-trash-alt align-middle" />
                                </button>
                            </ToolbarItem>
                        </ToolbarGroup>
                    )}
                    <ToolbarGroup>
                        <ToolbarItem>
                            <Select
                                className="preplanning-chart__select mr-2"
                                options={product_group_template_options}
                                value={selected_product_group}
                                theme={theme => ({
                                    ...theme,
                                    spacing: {
                                        ...theme.spacing,
                                        baseUnit: 2,
                                        controlHeight: 30,
                                    },
                                })}
                                isClearable
                                placeholder={translate("Preplanning.product_group_template", "Template name")}
                                onChange={this.handleGroupTemplateChange}
                            />
                        </ToolbarItem>
                        <ToolbarItem>
                            <MultiSelect
                                className="preplanning-chart__select mr-2"
                                options={product_group_lines_options}
                                value={this.state.selected_product_group_lines}
                                size={"sm"}
                                placeholder={translate("Preplanning.product_group_lines", "Product group lines")}
                                closeMenuOnSelect={false}
                                menuPosition="fixed"
                                onChange={this.handleProductGroupLineChange}
                            />
                        </ToolbarItem>
                        <ToolbarItem>
                            <MultiSelect
                                className="preplanning-chart__select mr-2"
                                options={group_options}
                                value={selected_material_groups}
                                size={"sm"}
                                placeholder={translate("Preplanning.product_group", "Product group")}
                                closeMenuOnSelect={false}
                                onChange={this.handleMaterialGroupChange}
                            />
                        </ToolbarItem>
                        <ToolbarItem>
                            <span className="flex-shrink-0 mr-2">
                                {translate("Production.expected_monthly_production", "Expected monthly production")}:
                            </span>
                            <strong>{expected_production >= 0 ? niceNumber(expected_production) : "N/A"}</strong>
                        </ToolbarItem>
                    </ToolbarGroup>
                </Toolbar>
                <HighchartsReact
                    highcharts={Highcharts}
                    options={this.getChartOptions()}
                    containerProps={{ className: "preplanning-chart__chart" }}
                />
                <Modal show={this.state.create_edit_product_group_modal} animation={false}>
                    <Modal.Header>
                        <Modal.Title>
                            {this.state.is_editing
                                ? translate("common.edit", "Edit")
                                : translate(
                                      "Preplanning.product_group_create_edit_template",
                                      "New product group template"
                                  )}
                        </Modal.Title>
                        <button
                            type="button"
                            className="close"
                            onClick={() => this.setState({ create_edit_product_group_modal: false })}
                        >
                            <span>×</span>
                        </button>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <div className="row align-items-center">
                            <div className="col">
                                <div className="form-row">
                                    <div className="col-auto mb-3 w-100">
                                        <label className="d-flex align-items-center mb-0">
                                            <input
                                                type="text"
                                                className="form-control w-100"
                                                placeholder={translate(
                                                    "Preplanning.product_group_name",
                                                    "Product group name"
                                                )}
                                                name="product_group_name"
                                                value={this.state.create_edit_product_group}
                                                onChange={e =>
                                                    this.setState({ create_edit_product_group: e.target.value })
                                                }
                                            />
                                        </label>
                                    </div>
                                    <div className="col-auto mb-3 w-100">
                                        <div className="d-flex align-items-center">
                                            <MultiSelect
                                                className="preplanning-chart__select max-width"
                                                options={this.props.lines.map(line => ({
                                                    label: line.title,
                                                    value: line.external_id,
                                                }))}
                                                value={this.state.create_edit_product_group_lines}
                                                placeholder={translate(
                                                    "Preplanning.product_group_lines",
                                                    "Product group lines"
                                                )}
                                                closeMenuOnSelect={false}
                                                menuPosition="fixed"
                                                onChange={(option: ILabelValuePair[]) =>
                                                    this.setState({ create_edit_product_group_lines: option })
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="col-auto d-flex justify-content-end w-100">
                                        <button
                                            className="btn btn-primary mr-2"
                                            onClick={this.handleNewGroupTemplateSave}
                                        >
                                            {translate("common.save", "Save")}
                                        </button>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={this.handleNewGroupTemplateClose}
                                        >
                                            {translate("common.cancel", "Cancel")}
                                        </button>
                                    </div>
                                </div>
                                <ErrorComponent msg={this.state.create_edit_product_group_error} type={"error"} />
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
                <Modal show={this.state.is_delete} animation={false} onHide={() => this.setState({ is_delete: false })}>
                    <Modal.Header>
                        <Modal.Title>
                            {translate("Preplanning.delete_product_group_template", "Delete product group template")}
                        </Modal.Title>
                        <button type="button" className="close" onClick={() => this.setState({ is_delete: false })}>
                            <span>×</span>
                        </button>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <div className="row align-items-center">
                            <div className="col">
                                <div className="form-row">
                                    <div className="col-auto mb-3 w-100">
                                        {translate(
                                            "Preplanning.delete_product_group_template_confirm",
                                            "Are you sure you want to delete this product group template?"
                                        )}
                                        <br />
                                        <strong>{this.state.selected_product_group}</strong>
                                    </div>
                                    <div className="col-auto d-flex justify-content-end w-100">
                                        <button
                                            className="btn btn-danger mr-2"
                                            onClick={this.handleDeleteGroupTemplate}
                                        >
                                            {translate("common.delete", "Delete")}
                                        </button>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => this.setState({ is_delete: false })}
                                        >
                                            {translate("common.cancel", "Cancel")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            </div>
        );
    }
}

export default PreplanningChart;
